/**
 * 'Report Section' data sources: an AI section can read other sections of the
 * same report. One entry per referenced section; `includeChildren: true` means
 * a parent ("category") was chosen and stands for it plus all descendants.
 * Mirrors sitrepSectionSources.ts on the COMS side.
 */

export const REPORT_SECTION_SOURCE = 'Report Section';

export type ReportSectionSourceEntry = {
  source: typeof REPORT_SECTION_SOURCE;
  id: string;
  sectionTemplateId: string | null;
  name: string;
  includeChildren: boolean;
};

export type SectionOutlineLike = {
  id: string;
  name: string;
  order?: number;
  section_template_id?: string | null;
  parent_section_id?: string | null;
};

type DataSourceLike = { source?: string; id?: string; [key: string]: unknown };

export function isReportSectionSource(
  dataSource: unknown
): dataSource is ReportSectionSourceEntry {
  return (
    !!dataSource &&
    typeof dataSource === 'object' &&
    (dataSource as DataSourceLike).source === REPORT_SECTION_SOURCE &&
    typeof (dataSource as DataSourceLike).id === 'string'
  );
}

/** Ids of the sections currently referenced (by id) in a data_sources array. */
export function selectedReportSectionIds(dataSources: unknown): Set<string> {
  const ids = new Set<string>();
  if (!Array.isArray(dataSources)) return ids;
  for (const ds of dataSources) {
    if (isReportSectionSource(ds)) ids.add(ds.id);
  }
  return ids;
}

/** The section itself plus everything nested under it. */
export function collectSectionSubtreeIds(
  sections: SectionOutlineLike[],
  rootId: string
): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const section of sections) {
    if (!section.parent_section_id) continue;
    const list = childrenByParent.get(section.parent_section_id) ?? [];
    list.push(section.id);
    childrenByParent.set(section.parent_section_id, list);
  }
  const seen = new Set<string>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const current = stack.pop() as string;
    for (const child of childrenByParent.get(current) ?? []) {
      if (seen.has(child)) continue;
      seen.add(child);
      stack.push(child);
    }
  }
  return seen;
}

/**
 * Sections a given section may reference: everything except itself and its own
 * descendants (a parent shell that contains the section would otherwise feed
 * the section its own text).
 */
export function selectableReportSections<T extends SectionOutlineLike>(
  sections: T[],
  currentSectionId: string | null | undefined
): T[] {
  if (!currentSectionId) return sections;
  const excluded = collectSectionSubtreeIds(sections, currentSectionId);
  return sections.filter((section) => !excluded.has(section.id));
}

export function makeReportSectionEntry(
  section: SectionOutlineLike,
  includeChildren: boolean
): ReportSectionSourceEntry {
  return {
    source: REPORT_SECTION_SOURCE,
    id: section.id,
    sectionTemplateId: section.section_template_id ?? null,
    name: section.name,
    includeChildren,
  };
}

/**
 * Add or remove one referenced section, leaving every non-'Report Section'
 * entry untouched. Choosing a parent with `includeChildren` drops any of its
 * descendants that were individually selected (they are covered by the
 * parent).
 */
export function toggleReportSectionRef(params: {
  dataSources: unknown;
  section: SectionOutlineLike;
  includeChildren: boolean;
  sections: SectionOutlineLike[];
}): unknown[] {
  const { dataSources, section, includeChildren, sections } = params;
  const current = Array.isArray(dataSources) ? [...dataSources] : [];
  const alreadySelected = current.some(
    (ds) => isReportSectionSource(ds) && ds.id === section.id
  );
  if (alreadySelected) {
    return current.filter(
      (ds) => !(isReportSectionSource(ds) && ds.id === section.id)
    );
  }
  const covered = includeChildren
    ? collectSectionSubtreeIds(sections, section.id)
    : new Set<string>([section.id]);
  const withoutCovered = current.filter(
    (ds) => !(isReportSectionSource(ds) && covered.has(ds.id))
  );
  return [...withoutCovered, makeReportSectionEntry(section, includeChildren)];
}

/**
 * Display label for a stored entry (falls back to the captured name when the
 * section is gone).
 */
export function reportSectionEntryLabel(
  entry: ReportSectionSourceEntry,
  sections?: SectionOutlineLike[]
): string {
  const live = sections?.find((section) => section.id === entry.id);
  const name = live?.name || entry.name || 'Section';
  return entry.includeChildren ? `${name} (all subsections)` : name;
}
